import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Plus, LogOut, Inbox, CalendarClock, MailQuestion } from "lucide-react";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";

const TYPES = [["share_a_story", "Share a story"], ["get_involved", "Get involved"], ["guidance", "Guidance"], ["media", "Media"], ["other", "Other"]];
const CHANNELS = ["website", "email", "phone", "social media", "in person"];
const STAGES = [["new", "New", "bg-amber-100 text-amber-800"], ["replied", "Replied", "bg-sky-100 text-sky-800"], ["in_conversation", "In conversation", "bg-[#1E4B3E]/10 text-[#1E4B3E]"], ["closed", "Closed", "bg-zinc-200 text-zinc-500"]];
const typeLabel = (t) => (TYPES.find(([k]) => k === t) || [t, t])[1];
const stageMeta = (s) => STAGES.find(([k]) => k === s) || STAGES[0];

export default function Tracker() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();
  const isTeam = user && ["admin", "team"].includes(user.role);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [filterStage, setFilterStage] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [note, setNote] = useState("");
  const [form, setForm] = useState({ first_name: "", channel: "website", type: "guidance", contact: "", detail: "" });

  const load = useCallback(async () => {
    try {
      const [list, sum, mem] = await Promise.all([api.get("/tracker"), api.get("/tracker/summary"), api.get("/team/members")]);
      setItems(list.data);
      setSummary(sum.data);
      setMembers(mem.data);
    } catch {}
  }, []);
  useEffect(() => { if (isTeam) load(); }, [isTeam, load]);

  if (!checked) return <div className="min-h-screen bg-[#F5F2EC] text-[#52525B] flex items-center justify-center text-sm">Loading…</div>;
  if (!isTeam) {
    return (
      <div className="min-h-screen bg-[#F5F2EC] flex flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-[#1A1A1A] font-grotesk">This tracker is for the core team only.</p>
        <Link to="/login" data-testid="tracker-login-link" className="bg-[#1E4B3E] text-white font-bold rounded-full px-6 py-2.5 text-sm">Sign in</Link>
      </div>
    );
  }

  const update = async (id, patch) => {
    try {
      await api.patch(`/tracker/${id}`, patch);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addNote = async (id) => {
    if (!note.trim()) return;
    try {
      await api.post(`/tracker/${id}/notes`, { text: note });
      setNote("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tracker", form);
      toast.success("Enquiry logged.");
      setForm({ first_name: "", channel: "website", type: "guidance", contact: "", detail: "" });
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const visible = items.filter((i) => (filterType === "all" || i.type === filterType) && (filterStage === "all" || i.stage === filterStage));

  return (
    <div className="min-h-screen bg-[#F5F2EC] text-[#1A1A1A]" data-testid="tracker-page">
      <header className="border-b border-[#E2DCD2] bg-white/70 backdrop-blur px-6 py-4 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/" className="flex items-center gap-3"><Logo size={28} /><span className="font-display font-extrabold uppercase text-sm">Enquiry tracker</span></Link>
        <div className="flex items-center gap-4">
          {user.role === "admin" && <Link to="/admin" className="text-xs text-[#52525B] hover:text-[#1A1A1A]" data-testid="tracker-admin-link">Stories &amp; pledges</Link>}
          <button onClick={async () => { await logout(); navigate("/"); }} className="flex items-center gap-1.5 text-xs text-[#52525B] hover:text-[#1A1A1A]"><LogOut size={13} /> Log out</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <p className="text-xs text-[#52525B] mb-8 max-w-2xl">Team only. First names and one contact detail at most — nothing clinical, no health claims; we connect people to professional support.</p>

        {summary && (
          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="bg-white border border-[#E2DCD2] rounded-2xl p-5"><Inbox size={16} className="text-[#C2410C] mb-2" /><p className="mono-label text-[#52525B]">New this week</p><p className="font-display font-extrabold text-3xl mt-1" data-testid="tracker-new-count">{summary.new_this_week}</p></div>
            <div className="bg-white border border-[#E2DCD2] rounded-2xl p-5"><CalendarClock size={16} className="text-[#1E4B3E] mb-2" /><p className="mono-label text-[#52525B]">Follow-ups due this week</p><p className="font-display font-extrabold text-3xl mt-1" data-testid="tracker-due-count">{summary.due_this_week}</p></div>
            <div className="bg-white border border-[#E2DCD2] rounded-2xl p-5"><MailQuestion size={16} className="text-[#B91C1C] mb-2" /><p className="mono-label text-[#52525B]">Awaiting a reply</p><p className="font-display font-extrabold text-3xl mt-1" data-testid="tracker-awaiting-count">{summary.awaiting_reply}</p></div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button data-testid="tracker-type-all" onClick={() => setFilterType("all")} className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${filterType === "all" ? "bg-[#1E4B3E] text-white" : "bg-white border border-[#E2DCD2] text-[#52525B]"}`}>All · {items.length}</button>
          {TYPES.map(([k, label]) => (
            <button key={k} data-testid={`tracker-type-${k}`} onClick={() => setFilterType(k)} className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${filterType === k ? "bg-[#1E4B3E] text-white" : "bg-white border border-[#E2DCD2] text-[#52525B]"}`}>
              {label} · {summary?.by_type?.[k] || 0}
            </button>
          ))}
          <button data-testid="tracker-add-button" onClick={() => setShowForm(!showForm)} className="ml-auto flex items-center gap-1.5 bg-[#1E4B3E] hover:bg-[#16382e] text-white rounded-full px-4 py-1.5 text-xs font-bold transition-colors"><Plus size={14} /> Log an enquiry</button>
        </div>
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setFilterStage("all")} className={`text-[11px] font-bold ${filterStage === "all" ? "text-[#1A1A1A] underline" : "text-[#52525B]"}`}>Every stage</button>
          {STAGES.map(([k, label]) => (
            <button key={k} data-testid={`tracker-stage-${k}`} onClick={() => setFilterStage(k)} className={`text-[11px] font-bold ${filterStage === k ? "text-[#1A1A1A] underline" : "text-[#52525B]"}`}>{label}</button>
          ))}
        </div>

        {showForm && (
          <form onSubmit={create} data-testid="tracker-form" className="bg-white border border-[#E2DCD2] rounded-2xl p-6 mb-6 grid sm:grid-cols-2 gap-3">
            <input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name only" data-testid="tracker-input-name" maxLength={60} className="bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1E4B3E]" />
            <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="tracker-select-channel" className="bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1E4B3E]">
              {CHANNELS.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
            </select>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="tracker-select-type" className="bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1E4B3E]">
              {TYPES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            </select>
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Contact (email or phone — kept private)" data-testid="tracker-input-contact" maxLength={160} className="bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1E4B3E]" />
            <input value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} placeholder="What they're asking for (brief)" data-testid="tracker-input-detail" maxLength={500} className="sm:col-span-2 bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#1E4B3E]" />
            <button type="submit" data-testid="tracker-submit-button" className="sm:col-span-2 bg-[#1E4B3E] hover:bg-[#16382e] text-white font-bold rounded-lg py-2.5 text-sm transition-colors">Log it</button>
          </form>
        )}

        <div className="space-y-2.5">
          {visible.length === 0 && <p className="text-sm text-[#52525B] bg-white border border-[#E2DCD2] rounded-2xl p-6">Nothing here yet. Website story and partnership submissions land here automatically.</p>}
          {visible.map((it) => {
            const [, sl, sc] = stageMeta(it.stage);
            const isOpen = expanded === it.id;
            return (
              <div key={it.id} data-testid={`tracker-item-${it.id}`} className="bg-white border border-[#E2DCD2] rounded-2xl p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-[10rem]">
                    <p className="font-display font-bold text-sm">{it.first_name}</p>
                    <p className="text-[11px] text-[#52525B]">{typeLabel(it.type)} · {it.channel} · {(it.date_received || "").slice(0, 10)}</p>
                  </div>
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${sc}`}>{sl}</span>
                  <div className="flex flex-wrap items-center gap-2 ml-auto">
                    <select value={it.stage} onChange={(e) => update(it.id, { stage: e.target.value })} data-testid={`tracker-stage-select-${it.id}`} className="bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-2 py-1.5 text-xs focus:outline-none">
                      {STAGES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                    <select value={it.assignee || ""} onChange={(e) => update(it.id, { assignee: e.target.value })}
                      data-testid={`tracker-assignee-${it.id}`}
                      className="bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-2 py-1.5 text-xs focus:outline-none max-w-[9rem]">
                      <option value="">Unassigned</option>
                      {members.map((m) => <option key={m.email} value={m.name}>{m.name}</option>)}
                      {it.assignee && !members.some((m) => m.name === it.assignee) && <option value={it.assignee}>{it.assignee}</option>}
                    </select>
                    <input type="date" value={it.follow_up_date || ""}
                      onChange={(e) => update(it.id, { follow_up_date: e.target.value })}
                      data-testid={`tracker-followup-${it.id}`}
                      className="bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
                    <button onClick={() => { setExpanded(isOpen ? null : it.id); setNote(""); }} data-testid={`tracker-expand-${it.id}`} className="text-[#52525B] hover:text-[#1A1A1A]" aria-label="Details">
                      <ChevronDown size={16} className={`transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="mt-4 pt-4 border-t border-[#E2DCD2] grid sm:grid-cols-2 gap-4">
                    <div>
                      {it.detail && <p className="text-xs text-[#52525B] leading-relaxed mb-2">{it.detail}</p>}
                      {it.contact && <p className="text-xs"><span className="mono-label text-[#52525B]">Contact (private):</span> <span className="font-grotesk">{it.contact}</span></p>}
                      <p className="text-[10px] text-[#A1A1AA] mt-2">Source: {it.source} · Received {(it.date_received || "").slice(0, 10)}</p>
                      <button onClick={async () => { await api.delete(`/tracker/${it.id}`); toast.success("Enquiry removed."); load(); }}
                        data-testid={`tracker-delete-${it.id}`} className="mt-3 text-[10px] font-bold text-[#A1A1AA] hover:text-[#B91C1C] transition-colors">Remove this enquiry</button>
                    </div>
                    <div>
                      <p className="mono-label text-[#52525B] mb-2">Team notes (private)</p>
                      <div className="space-y-2 mb-3 max-h-36 overflow-y-auto">
                        {(it.notes || []).map((n) => (
                          <div key={n.id} className="bg-[#F5F2EC] rounded-lg px-3 py-2">
                            <p className="text-xs leading-relaxed">{n.text}</p>
                            <p className="text-[10px] text-[#A1A1AA] mt-1">{n.author} · {(n.created_at || "").slice(0, 10)}</p>
                          </div>
                        ))}
                        {(it.notes || []).length === 0 && <p className="text-[11px] text-[#A1A1AA]">No notes yet.</p>}
                      </div>
                      <div className="flex gap-2">
                        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note…" maxLength={1000}
                          data-testid={`tracker-note-input-${it.id}`}
                          className="flex-1 bg-[#F5F2EC] border border-[#E2DCD2] rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#1E4B3E]" />
                        <button onClick={() => addNote(it.id)} data-testid={`tracker-note-add-${it.id}`} className="bg-[#1E4B3E] text-white rounded-lg px-3 py-2 text-xs font-bold">Add</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
